import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CountryISO, SearchCountryField, } from '@justin-s/ngx-intl-tel-input';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FlagService } from '../../../../shared/services/flag.service';
import { FlagType } from '../../../../shared/utils/enums';
import { civilityOptions } from '../../../../shared/utils/data/etablissments-data';
import { AgencyUserPayload, AgencyUsersService } from '../../../../services/agency-users.service';
import {
  AgencyPermission,
  EXTRA_PERMISSIONS,
  MATRIX_PERMISSIONS,
  PERMISSION_LABELS,
  ROLE_PERMISSIONS,
  SELECTABLE_ROLES,
} from '../../../../shared/utils/agency-rbac';
import { PermissionService } from '../../../../services/permission.service';
import { PermissionInfoDialogComponent } from '../../../../shared/components/permission-info-dialog/permission-info-dialog.component';

/**
 * Invite a collaborator (data = null) or edit one (data = the collaborator row).
 * Inviting creates a PENDING account and e-mails an activation link; no password is set here.
 */
@Component({
  selector: 'app-add-user',
  templateUrl: './add-user.component.html',
  styleUrl: './add-user.component.scss'
})
export class AddUserComponent implements OnInit {
  preferredCountries: CountryISO[] = [CountryISO.France];
  form: FormGroup;
  loading = false;
  images: string[] = [];
  phoneMaxLength: number = 10;
  phoneNumber: string | undefined;
  errorMessage = '';

  /**
   * Lot G (G1) : 5 rôles collaborateurs proposés (l'admin n'est pas sélectionnable). Anti-escalade (miroir du
   * back) : un non-administrateur ne voit que les rôles dont les droits par défaut sont inclus dans les siens
   * (un assistant ne peut donc pas créer un Responsable RH) ; le rôle actuel du compte édité reste affiché.
   */
  get roles() {
    if (this.permissionService.isAdmin) {
      return SELECTABLE_ROLES;
    }
    return SELECTABLE_ROLES.filter((r) =>
      r.value === this.data?.role
      || (ROLE_PERMISSIONS[r.value] ?? []).every((p) => this.permissionService.has(p)));
  }

  // Lot G (G4) : toggles on/off par utilisateur — ne modifient QUE ce compte (Q6).
  /** Tous les droits connus (état complet envoyé au back). */
  readonly permissionKeys = Object.values(AgencyPermission);
  /** Les 11 droits de la matrice du PDF, dans l'ordre du PDF (§4 « Gestion des droits des collaborateurs »). */
  readonly matrixKeys = MATRIX_PERMISSIONS;
  /** Droits hors matrice (relevés d'heures / paie, audit) : affichés comme la matrice ; seule l'escalade est bloquée. */
  readonly extraKeys = EXTRA_PERMISSIONS;
  readonly permissionLabels = PERMISSION_LABELS;
  permissionState: Record<string, boolean> = {};
  permissionsLoaded = false;
  permissionsDirty = false;

  /**
   * Modifiables seulement avec le droit « Modifier les droits d'un compte » (USER_MANAGE) et hors dirigeant
   * principal (admin non configurable). Sans ce droit (ex. assistant qui invite), les droits du rôle s'appliquent.
   */
  get permissionsEditable(): boolean {
    return !this.roleLocked && !this.isSelf && this.permissionService.has(AgencyPermission.USER_MANAGE);
  }

  /** Édition de SON PROPRE compte : ni le rôle ni les droits ne sont modifiables (le serveur refuse aussi). */
  get isSelf(): boolean {
    return this.isEdit && !!this.data?.self;
  }

  get isAdmin(): boolean {
    return this.permissionService.isAdmin;
  }

  /** « Autre » : le nom de poste est obligatoire (PDF §5 : l'admin donne le nom du poste de son choix). */
  get isAutre(): boolean {
    return this.form?.get('role')?.value === 'AUTRE';
  }

  /** Anti-escalade : on ne peut ACTIVER que ce qu'on détient soi-même (le back refuse sinon). */
  canToggle(key: string): boolean {
    return this.permissionsEditable
      && (this.isAdmin || this.permissionService.has(key) || !!this.permissionState[key]);
  }

  togglePermission(key: string): void {
    if (!this.canToggle(key)) {
      return;
    }
    this.permissionState[key] = !this.permissionState[key];
    this.permissionsDirty = true;
  }

  /** Applique les défauts du rôle choisi tant qu'aucun toggle n'a été touché (Q6). */
  onRoleChange(): void {
    // « Autre » → nom de poste obligatoire ; sinon simple champ facultatif.
    const fonction = this.form?.get('fonction');
    if (fonction) {
      fonction.setValidators(this.isAutre
        ? [Validators.required, Validators.maxLength(100)]
        : [Validators.maxLength(100)]);
      fonction.updateValueAndValidity({ emitEvent: false });
    }
    if (this.permissionsDirty) {
      return;
    }
    const role = this.form?.get('role')?.value;
    const defaults = ROLE_PERMISSIONS[role as keyof typeof ROLE_PERMISSIONS] ?? [];
    const next: Record<string, boolean> = {};
    for (const key of this.permissionKeys) {
      next[key] = defaults.includes(key);
    }
    this.permissionState = next;
  }
  protected readonly SearchCountryField = SearchCountryField;
  protected readonly CountryISO = CountryISO;
  protected readonly civilityOptions = civilityOptions;

  constructor(
    public dialogRef: MatDialogRef<AddUserComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private usersService: AgencyUsersService,
    private flag: FlagService,
    private permissionService: PermissionService,
    private dialog: MatDialog,
  ) {}

  /** Bulle « i » : ouvre la liste des fonctionnalités d'un droit (sans basculer l'interrupteur de la ligne). */
  openPermissionInfo(key: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.dialog.open(PermissionInfoDialogComponent, {
      data: { permission: key },
      autoFocus: false,
      maxWidth: '92vw',
    });
  }

  get isEdit(): boolean {
    return !!this.data?.id;
  }

  /** The login email becomes immutable once the account is activated (it is also the 2FA channel). */
  get emailLocked(): boolean {
    return this.isEdit && this.data?.status !== 'PENDING';
  }

  get roleLocked(): boolean {
    return this.isEdit && !!this.data?.mainAccount;
  }

  get selectedRoleDescription(): string {
    const value = this.form?.get('role')?.value;
    return this.roles.find((r) => r.value === value)?.description ?? '';
  }

  ngOnInit() {
    this.form = this.fb.group({
      title: [''],
      firstName: ['', [Validators.required, Validators.maxLength(100)]],
      lastName: ['', [Validators.required, Validators.maxLength(100)]],
      mail: ['', [Validators.required, Validators.email, Validators.maxLength(50)]],
      numTel: [''],
      fonction: ['', Validators.maxLength(100)],
      role: [this.roles[0]?.value ?? 'ASSISTANT_SECRETAIRE', Validators.required],
      photo1: [''],
    });

    if (this.isEdit) {
      this.phoneNumber = this.data?.phoneNumber;
      this.form.patchValue({
        title: this.data.title ?? '',
        firstName: this.data.firstName ?? '',
        lastName: this.data.lastName ?? '',
        mail: this.data.email ?? '',
        numTel: this.data.phoneNumber ?? '',
        fonction: this.data.fonction ?? '',
        role: this.data.role ?? 'RECRUTEUR',
      });
      if (this.data.image) {
        this.images = [this.data.image];
      }
      if (this.emailLocked) {
        this.form.get('mail')?.disable();
      }
      if (this.roleLocked || this.isSelf) {
        this.form.get('role')?.disable();
      }
    }

    // Lot G (G4) : état initial des toggles = état serveur (édition) ou défauts du rôle
    // pré-sélectionné (création). Le changement de rôle ré-applique les défauts tant que
    // l'admin n'a pas touché un toggle (Q6 : « Autre » = tout off).
    if (this.isEdit) {
      this.permissionsLoaded = false;
      this.usersService.getPermissions(this.data.id).subscribe({
        next: (res) => {
          this.permissionState = { ...res.permissions };
          this.permissionsLoaded = true;
        },
        error: () => {
          this.onRoleChange();
          this.permissionsLoaded = true;
        },
      });
    } else {
      this.onRoleChange();
      this.permissionsLoaded = true;
    }
    this.form.get('role')?.valueChanges.subscribe(() => this.onRoleChange());
    // applique tout de suite la règle « nom de poste obligatoire pour Autre » au rôle pré-sélectionné
    this.onRoleChange();
  }

  getImage(images: string[]) {
    this.form.get('photo1')?.patchValue(images?.[0] ?? '');
  }

  onInputChange(event: any) {
    const inputNumber = event?.number;
    if (!inputNumber) return;
    this.phoneMaxLength = inputNumber.startsWith('0') ? 10 : 9;
    this.phoneNumber = inputNumber.length > this.phoneMaxLength ? inputNumber.slice(0, this.phoneMaxLength) : inputNumber;
  }

  private phoneValue(): string | undefined {
    const raw = this.form.get('numTel')?.value;
    if (!raw) {
      return undefined;
    }
    if (typeof raw === 'string') {
      return raw.replace(/\s/g, '') || undefined;
    }
    return (raw.e164Number || raw.number || '').replace(/\s/g, '') || undefined;
  }

  private payload(): AgencyUserPayload {
    const v = this.form.getRawValue();
    const image = v.photo1 ? v.photo1 : undefined;
    return {
      email: (v.mail ?? '').trim(),
      firstName: (v.firstName ?? '').trim(),
      lastName: (v.lastName ?? '').trim(),
      title: v.title || undefined,
      phoneNumber: this.phoneValue(),
      fonction: (v.fonction ?? '').trim() || undefined,
      role: v.role,
      image,
    };
  }

  /** Lot G (G4) : envoie les toggles au back après la création/l'édition du compte. */
  private savePermissions(userId: number, onDone: () => void, onError: (err: any) => void) {
    if (!this.permissionsEditable) {
      onDone();
      return;
    }
    this.usersService.updatePermissions(userId, this.permissionState).subscribe({
      next: () => onDone(),
      error: (err) => onError(err),
    });
  }

  submit() {
    if (this.form.invalid || this.loading) {
      this.form.markAllAsTouched();
      return;
    }
    this.errorMessage = '';
    this.isEdit ? this.updateUser() : this.inviteUser();
  }

  private inviteUser() {
    this.loading = true;
    this.usersService.invite(this.payload()).subscribe({
      next: (user) => {
        // Lot G (G4) : les toggles sont appliqués juste après la création du compte.
        this.savePermissions(
          user.id,
          () => {
            this.loading = false;
            this.flag.displayFlag(`Invitation envoyée à ${user.email}.`, FlagType.success);
            this.dialogRef.close(true);
          },
          (err) => {
            this.loading = false;
            this.flag.displayFlag(
              `Invitation envoyée mais les droits n'ont pas pu être enregistrés : ${AgencyUsersService.errorMessage(err)}`,
              FlagType.error
            );
            this.dialogRef.close(true);
          }
        );
      },
      error: (err) => {
        this.loading = false;
        this.errorMessage = AgencyUsersService.errorMessage(err, "L'invitation n'a pas pu être envoyée.");
      },
    });
  }

  private updateUser() {
    this.loading = true;
    const payload = this.payload();
    const newRole = payload.role;
    const { role, ...profile } = payload;
    if (this.emailLocked) {
      delete profile.email;
    }

    this.usersService.update(this.data.id, profile).subscribe({
      next: () => {
        const roleChanged = !this.roleLocked && !this.isSelf && newRole && newRole !== this.data.role;
        const applyPermissions = () => {
          if (!this.permissionsDirty) {
            this.done('Collaborateur modifié.');
            return;
          }
          this.savePermissions(
            this.data.id,
            () => this.done('Collaborateur modifié.'),
            (err) => {
              this.loading = false;
              this.usersService.refresh();
              this.errorMessage = AgencyUsersService.errorMessage(err, "Les droits n'ont pas pu être enregistrés.");
            }
          );
        };
        if (roleChanged) {
          this.usersService.changeRole(this.data.id, newRole).subscribe({
            next: () => applyPermissions(),
            error: (err) => {
              this.loading = false;
              this.usersService.refresh();
              this.errorMessage = AgencyUsersService.errorMessage(err, "Le rôle n'a pas pu être modifié.");
            },
          });
        } else {
          applyPermissions();
        }
      },
      error: (err) => {
        this.loading = false;
        this.errorMessage = AgencyUsersService.errorMessage(err, "Les modifications n'ont pas pu être enregistrées.");
      },
    });
  }

  private done(message: string) {
    this.loading = false;
    this.flag.displayFlag(message, FlagType.success);
    this.usersService.refresh();
    this.dialogRef.close(true);
  }
}
